import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { motion, AnimatePresence } from 'motion/react'
import type { MortgageInput, MortgageResult } from '@/lib/mortgage'
import { formatPLN, formatPercent } from '@/lib/constants'

export interface Scenario {
  id: number
  input: MortgageInput
  result: MortgageResult
}

interface ScenarioComparisonProps {
  scenarios: Scenario[]
  onSave: () => void
  onClear: () => void
  canSave: boolean
}

export function ScenarioComparison({
  scenarios,
  onSave,
  onClear,
  canSave,
}: ScenarioComparisonProps) {
  const { t } = useTranslation()

  return (
    <motion.div
      className="space-y-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">{t('scenarios.title')}</h2>
        <div className="flex gap-2">
          <Button
            onClick={onSave}
            disabled={!canSave}
            size="sm"
            className="bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] hover:opacity-90 transition-opacity"
          >
            {t('scenarios.save')}
          </Button>
          {scenarios.length > 0 && (
            <Button onClick={onClear} variant="outline" size="sm">
              {t('scenarios.clear')}
            </Button>
          )}
        </div>
      </div>

      {scenarios.length === 0 ? (
        <Card className="border-dashed border-2 border-indigo-200/50">
          <CardContent className="py-10 text-center">
            <div className="text-4xl mb-3">📊</div>
            <p className="text-muted-foreground text-sm">
              {t('scenarios.empty')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-3">
          <AnimatePresence>
            {scenarios.map((scenario, index) => (
              <motion.div
                key={scenario.id}
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -10 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Card className="h-full card-hover group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-violet-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <CardContent className="py-4 px-4 space-y-3 relative">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] text-white text-xs font-bold flex items-center justify-center">
                        {index + 1}
                      </span>
                      <span className="text-sm font-medium text-foreground">
                        {t('scenarios.scenario')} {index + 1}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {t('results.monthlyPayment')}
                        </span>
                        <span className="font-bold bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] bg-clip-text text-transparent">
                          {formatPLN(scenario.result.monthlyPayment)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {t('results.loanAmount')}
                        </span>
                        <span className="font-medium">
                          {formatPLN(scenario.result.loanAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {t('results.totalInterest')}
                        </span>
                        <span className="font-medium text-rose-600">
                          {formatPLN(scenario.result.totalInterest)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {t('results.interestRateLabel')}
                        </span>
                        <span className="font-medium">
                          {formatPercent(scenario.result.annualRate)}
                        </span>
                      </div>
                      <div className="pt-2 border-t text-xs text-muted-foreground">
                        {formatPLN(scenario.input.propertyPrice)} &middot;{' '}
                        {scenario.input.downPaymentPercent}% &middot;{' '}
                        {scenario.input.loanYears} {t('calculator.years')}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  )
}
